import { useEffect, useState } from "react";
import { useMutation, useQuery, useInfiniteQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  createCampaign,
  duplicateCampaign,
  importCampaign,
  getCampaigns,
  getCampaignById,
  updateCampaign,
  getCampaignIntegrations,
  updateCampaignIntegrations,
  getCampaignReadiness,
  updateCampaignNotificationEmail,
  deleteCampaign,
  bulkDeleteCampaigns,
  fetchCampaignIndividualSequencingContent,
  getContentWriterStatus,
  updateCampaignIndividualSequencingContent,
  addCampaignAsset,
  deleteCampaignAsset,
  getAnalyticsAggregatedDataForCampaignDashboard,
  campaignStatusAction,
  getAnalyticsAggregatedDataForCampaignDashboardByChannel,
  getCampaignContentStatus,
  generateMissingCampaignContent,
  getSequenceTransitions,
  saveSequenceFallback,
  deleteSequenceTransition,
} from "../api/campaignsApi";
import {
  getCampaignSteps,
  createCampaignStep,
  updateCampaignStep,
  deleteCampaignStep,
  deleteIndividualSequenceStep,
  reorderCampaignSteps,
  updateCampaignStepAdvanceSettings,
  updateCampaignStepContentUserChanges,
  saveLeadMessage,
  resetLeadMessage,
} from "../api/campaignStepsApi";
import {
  moveContactsBatch,
  getCampaignLeads,
  deleteLead,
  subscribeEnrollmentBatchEvents,
  getEnrollmentBatch,
} from "../api/enrollmentsApi";
import { showSuccess, showError, showWarning, toast, showErrorFromMutation } from "@/shared/utils/toast";
import { useInfiniteScroll } from "@/shared/hooks/useInfiniteScroll";
import { useMainStore } from "@/stores";
import { InfoIcon } from "lucide-react";
import { startEnrollmentBatchFromResponse } from "@/features/campaigns/utils/enrollmentBatchToast";
import { trialLeadSkipMessage } from "@/shared/utils/trialLimits";

/**
 * Navigation-driven lists and detail views. These previously used staleTime: 0 and
 * refetchOnMount: "always", so returning to a screen was always a cold network load.
 * Writes still show up immediately because the mutations invalidate their query keys.
 * Deliberately shorter than the 5-minute app-wide default in App.jsx, since campaign
 * counters move on their own as sequences run.
 */
const LIST_STALE_TIME = 30_000;

/**
 * Generated sequence content only changes when generation finishes or the user
 * edits it - both of which either poll or invalidate this key. Stepping to the
 * next prospect and back should therefore be instant, not a network round trip.
 */
const SEQUENCE_CONTENT_STALE_TIME = 60_000;

// create new camapigns hook
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCampaign,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Campaign created successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to create campaign");
    },
  });
};

export const useDuplicateCampaign = ({ action } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId }) => duplicateCampaign(campaignId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Campaign duplicated successfully!");
      action?.(data);
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to duplicate campaign");
    },
  });
};

export const useImportCampaign = ({ action } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: importCampaign,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Campaign imported. Add leads to start it.");
      action?.(data);
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to import campaign");
    },
  });
};

// update campaign details hook
export const useUpdateCampaign = (campaignId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, campaignData }) =>
      updateCampaign(campaignId, campaignData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      // Campaign messages / content settings change what each lead's sequence shows.
      queryClient.invalidateQueries({ queryKey: ["campaign-individual-sequencing-content"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Campaign updated successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update campaign");
    },
  });
};

export const useUpdateCampaignNotificationEmail = (campaignId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId: id, notificationEmail }) =>
      updateCampaignNotificationEmail(id, notificationEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Notification email updated successfully!");
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to update notification email",
      );
    },
  });
};

//  get campaign list hook
export const useGetCampaigns = (
  page = 1,
  limit = 10,
  searchQuery = "",
  enabled = true,
) => {
  return useQuery({
    queryKey: ["campaigns", page, limit, searchQuery],
    queryFn: () => getCampaigns(page, limit, searchQuery),
    enabled,
    // Was staleTime: 0, so every navigation back to the list was a cold load.
    // Freshness after writes comes from the invalidateQueries(["campaigns"]) calls
    // in the mutations below, not from refetching on every mount.
    staleTime: LIST_STALE_TIME,
    retry: 1,
    placeholderData: keepPreviousData,
  });
};

/**
 * True once we know the user has no campaigns (false while loading). Match scores are
 * measured against campaigns, so scoring is disabled until one exists; the server checks too.
 */
export const useHasNoCampaigns = () => {
  const { data } = useGetCampaigns(1, 1, "");
  return data != null && (data.totalCount ?? 0) === 0;
};

export const NO_CAMPAIGNS_SCORE_HINT =
  "Create a campaign first. Match scores are calculated against your campaigns' audience.";

// infinite scroll campaigns hook
export const useInfiniteGetCampaigns = (
  limit = 10,
  searchQuery = "",
  enabled = true
) => {
  const infiniteQuery = useInfiniteQuery({
    queryKey: ["campaigns-infinite", limit, searchQuery],
    queryFn: ({ pageParam }) => getCampaigns(pageParam, limit, searchQuery),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage?.totalCount ?? 0;
      const loadedCount = allPages.reduce(
        (sum, p) => sum + (p?.campaigns?.length ?? 0),
        0
      );
      return loadedCount < total ? (lastPage?.page ?? allPages.length) + 1 : undefined;
    },
    enabled,
    staleTime: LIST_STALE_TIME,
    retry: 1,
  });
  return useInfiniteScroll(infiniteQuery, (page) => page?.campaigns ?? []);
};

//  get single campaign details hook
export const useGetCampaignById = (campaignId, enabled = true) => {
  return useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: () => getCampaignById(campaignId),
    enabled: enabled && !!campaignId,
    staleTime: LIST_STALE_TIME,
  });
};

export const useGetCampaignIntegrations = (campaignId, enabled = true) => {
  return useQuery({
    queryKey: ["campaign-integrations", campaignId],
    queryFn: () => getCampaignIntegrations(campaignId),
    enabled: enabled && !!campaignId,
    staleTime: LIST_STALE_TIME,
  });
};

export const useGetCampaignReadiness = (campaignId, enabled = true) => {
  return useQuery({
    queryKey: ["campaign-readiness", campaignId],
    queryFn: () => getCampaignReadiness(campaignId),
    enabled: enabled && !!campaignId,
    staleTime: 15_000,
  });
};

export const useUpdateCampaignIntegrations = (campaignId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      const targetCampaignId = variables?.campaignId || campaignId;
      const payload = variables?.payload || variables;
      return updateCampaignIntegrations(targetCampaignId, payload);
    },
    onSuccess: (data, variables) => {
      const targetCampaignId = variables?.campaignId || campaignId;
      queryClient.setQueryData(["campaign-integrations", targetCampaignId], data);
      queryClient.invalidateQueries({ queryKey: ["campaign", targetCampaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaign-readiness", targetCampaignId] });
      showSuccess("Campaign integrations updated successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to update campaign integrations",
      );
    },
  });
};

//  delete campaigns hook
export const useDeleteCampaign = ({ action }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId }) => deleteCampaign(campaignId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Campaign deleted successfully!");
      if (action) {
        action();
      }
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update campaign");
    },
  });
};

// bulk delete campaigns hook
export const useBulkDeleteCampaigns = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignIds }) => bulkDeleteCampaigns(campaignIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns-infinite"] });
      showSuccess("Campaigns deleted successfully!");
      if (onSuccess) onSuccess();
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to delete campaigns");
    },
  });
};

//  fetch campaign steps hook
export const useGetCampaignSteps = (campaignId, enabled = true) => {
  return useQuery({
    queryKey: ["campaign-steps", campaignId],
    queryFn: () => getCampaignSteps(campaignId),
    enabled: enabled && !!campaignId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useGetSequenceTransitions = (fromStepId, enabled = true) => {
  return useQuery({
    queryKey: ["sequence-transitions", fromStepId],
    queryFn: () => getSequenceTransitions(fromStepId),
    enabled: enabled && !!fromStepId,
    staleTime: 30_000,
  });
};

export const useSaveSequenceFallback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveSequenceFallback,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["sequence-transitions", variables.fromStepId],
      });
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to save campaign branch");
    },
  });
};

export const useDeleteSequenceTransition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transitionId }) => deleteSequenceTransition(transitionId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["sequence-transitions", variables.fromStepId],
      });
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to reset campaign branch");
    },
  });
};

// create campaign step hook
export const useCreateCampaignStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, stepData }) =>
      createCampaignStep(campaignId, stepData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-steps", variables.campaignId],
      });
      showSuccess("Step added successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to create step");
    },
  });
};

// update campaign step hook
export const useUpdateCampaignStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, stepId, stepData }) =>
      updateCampaignStep(campaignId, stepId, stepData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-steps", variables.campaignId],
      });
      showSuccess("Step updated successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update step");
    },
  });
};

// update campaign step advance settings hook
export const useDeleteCampaignStep = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId }) => deleteCampaignStep({ stepId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-steps"] });
      showSuccess("Step deleted successfully!");
      if (action) {
        action();
      }
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to delete step");
    },
  });
};

export const useDeleteIndividualSequenceStep = ({ action } = {}) => {
  return useMutation({
    mutationFn: ({ stepId, enrollmentId }) =>
      deleteIndividualSequenceStep({ stepId, enrollmentId }),
    onSuccess: (data) => {
      showSuccess("Step deleted from individual sequence successfully!");
      action?.();
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to delete individual sequence step",
      );
    },
  });
};

// reorder campaign steps hook
export const useReorderCampaignSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, stepsOrder }) =>
      reorderCampaignSteps(campaignId, stepsOrder),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-steps", variables.campaignId],
      });
      showSuccess("Steps reordered successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to reorder steps");
    },
  });
};

// ppdate campaign step advance setting hook
export const useUpdateCampaignStepAdvanceSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, advanceSettingsData }) =>
      updateCampaignStepAdvanceSettings(stepId, advanceSettingsData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-steps"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-individual-sequencing-content"] });
      showSuccess(variables.successMessage || "Advance settings updated successfully!");
      // Call action callback if provided (e.g., onClose)
      if (variables.action) {
        variables.action();
      }
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update advance settings");
    },
  });
};

/** Save / reset a message edited for one lead in Individual Sequences. */
export const useSaveLeadMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveLeadMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-individual-sequencing-content"] });
      showSuccess("Message updated for this lead.");
    },
    onError: (error) => showErrorFromMutation(error, error.message),
  });
};

export const useResetLeadMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetLeadMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-individual-sequencing-content"] });
      showSuccess("This lead is back on the shared message.");
    },
    onError: (error) => showErrorFromMutation(error, error.message),
  });
};

// add leads to campaign hook
export const useAddLeadsToCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, contacts, mode = "campaign" }) =>
      moveContactsBatch({ mode, campaignId, contacts }),
    onSuccess: (data, variables) => {
      const batchMode = variables?.mode ?? data?.mode ?? "campaign";
      startEnrollmentBatchFromResponse(data, {
        campaignId: variables?.campaignId,
        mode: batchMode,
      });

      if (variables?.campaignId) {
        queryClient.invalidateQueries({
          queryKey: ["campaign-leads", variables.campaignId],
        });
        queryClient.invalidateQueries({ queryKey: ["campaign-leads-infinite"] });
      }

      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["contact-groups"] });

      if (batchMode === "account" || batchMode === "both") {
        queryClient.invalidateQueries({ queryKey: ["accounts"] });
      }

      if (!variables?.suppressEnrollmentToasts) {
        const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
        const emailSkipped = skipped.filter((item) =>
          String(item?.reason || "").startsWith("EMAIL_"),
        );
        const trialMessage = trialLeadSkipMessage(skipped);
        if (trialMessage) {
          showWarning(trialMessage);
        } else if (skipped.length === 0) {
          showSuccess(
            `${variables.contacts.length} contact${variables.contacts.length !== 1 ? "s" : ""} added to campaign successfully!`,
          );
        } else if (data?.skipped?.length > 0 && data?.data?.length > 0) {
          toast.warn(
            <div className="flex items-start gap-3 rounded-xl border border-[var(--border-color-light)] bg-[var(--bg-standard)] px-4 py-3 shadow-[0_10px_40px_-12px_rgba(37,43,55,0.18)]">
              <InfoIcon
                size={18}
                strokeWidth={1.7}
                className="shrink-0 text-[var(--text-warning)] mt-0.5"
              />
              <p className="text-[0.875rem] font-medium text-[var(--text-color)] leading-snug">
                {skipped.length} contact
                {skipped.length !== 1 ? "s were" : " was"} skipped
                {emailSkipped.length > 0
                  ? ` because ${emailSkipped.length} had an undeliverable email${emailSkipped.length !== 1 ? "s" : ""}.`
                  : " (already in this campaign)."}
              </p>
            </div>,
          );
        } else {
          showError(
            emailSkipped.length > 0
              ? `${emailSkipped.length} contact${emailSkipped.length !== 1 ? "s were" : " was"} skipped because the email ${emailSkipped.length !== 1 ? "addresses are" : "address is"} not deliverable.`
              : `${skipped.length} contact${skipped.length !== 1 ? "s" : ""} skipped already in campaign!`,
          );
        }
      }

      if (variables.action) {
        variables.action();
      }
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to add contacts to campaign", {
        toastId: "add-leads-error",
      });
    },
  });
};

export const useEnrollmentBatch = ({
  batchJobId,
  campaignId,
  mode = "campaign",
  enabled = true,
}) => {
  const queryClient = useQueryClient();
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(null);

  const snapshotQuery = useQuery({
    queryKey: ["enrollment-batch", batchJobId],
    queryFn: () => getEnrollmentBatch(batchJobId),
    enabled: enabled && !!batchJobId,
    staleTime: 2000,
    retry: 1,
    refetchInterval: (query) => {
      const snapshot = query.state.data;
      if (!snapshot || snapshot.progress?.status === "COMPLETED") return false;
      if (snapshot.state === "failed") return false;
      return 2000;
    },
  });

  useEffect(() => {
    if (!enabled || !batchJobId) return undefined;
    const unsubscribe = subscribeEnrollmentBatchEvents(batchJobId, {
      onEvent: (event) => {
        setEvents((prev) => [...prev.slice(-49), event]);
        setStatus(event);

        if (
          event.type === "batch.progress" ||
          event.type === "batch.completed" ||
          event.type === "batch.snapshot"
        ) {
          queryClient.setQueryData(["enrollment-batch", batchJobId], (old) => ({
            ...(old || {}),
            ...event.data,
            success: true,
          }));
        }

        if (event.type === "batch.completed") {
          if (campaignId) {
            queryClient.invalidateQueries({
              queryKey: ["campaign-leads", campaignId],
            });
            queryClient.invalidateQueries({ queryKey: ["campaign-leads-infinite"] });
          }
          if (mode === "account" || event.data?.mode === "account") {
            queryClient.invalidateQueries({ queryKey: ["accounts"] });
          }
        }
      },
    });
    return unsubscribe;
  }, [batchJobId, campaignId, enabled, mode, queryClient]);

  return {
    events,
    status,
    snapshot: snapshotQuery.data,
    isLoading: snapshotQuery.isLoading,
  };
};

//  fetch campaign leads hook
export const useFetchCampaignLeads = ({
  campaignId,
  page = 1,
  limit = 10,
  searchQuery = "",
  enabled = true,
  // Paginated callers show live counters and want the 15s poll. Callers that
  // pull a whole campaign's leads only to navigate between them should pass
  // false rather than re-download the full list every 15 seconds.
  refetchInterval = 15_000,
}) => {
  return useQuery({
    queryKey: ["campaign-leads", campaignId, page, limit, searchQuery],
    queryFn: () => getCampaignLeads(campaignId, page, limit, searchQuery),
    enabled: enabled && !!campaignId,
    // refetchOnMount: "always" was silently defeating this staleTime.
    staleTime: 5 * 60 * 1000,
    refetchInterval,
    retry: 1,
    placeholderData: keepPreviousData,
  });
};

// infinite scroll campaign leads hook
export const useInfiniteFetchCampaignLeads = ({
  campaignId,
  limit = 10,
  searchQuery = "",
  enabled = true,
}) => {
  const infiniteQuery = useInfiniteQuery({
    queryKey: ["campaign-leads-infinite", campaignId, limit, searchQuery],
    queryFn: ({ pageParam }) =>
      getCampaignLeads(campaignId, pageParam, limit, searchQuery),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage?.total ?? 0;
      const loadedCount = allPages.reduce(
        (sum, p) => sum + (p?.leads?.length ?? 0),
        0
      );
      return loadedCount < total ? (lastPage?.page ?? allPages.length) + 1 : undefined;
    },
    enabled: enabled && !!campaignId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 15_000,
    retry: 1,
  });
  const getId = (item) => item?.id ?? item?.contact_id ?? item?.email;
  return useInfiniteScroll(infiniteQuery, (page) => page?.leads ?? [], getId);
};

/** GET /campaign/:id/content-status — allLeadsReady, counts, per-lead gaps. */
export const useCampaignContentStatus = (campaignId, enabled = true) => {
  const isGenerating = useMainStore.getState().isGeneratingCampaignContent;
  return useQuery({
    queryKey: ["campaign-content-status", campaignId],
    queryFn: () => getCampaignContentStatus(campaignId),
    enabled: !!campaignId && enabled,
    refetchInterval: isGenerating ? 8000 : false,
    staleTime: 15 * 1000,
    retry: 1,
  });
};

/** POST /campaign/:id/generate-missing-content — fills missing AI templates for all enrollments. */
export const useGenerateMissingCampaignContent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId) => {
      useMainStore.getState().setIsGeneratingCampaignContent(true);
      return generateMissingCampaignContent(campaignId);
    },
    onSuccess: (data, campaignId) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-content-status", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign-individual-sequencing-content"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["campaign-leads"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-leads-infinite"] });
      const msg =
        typeof data?.message === "string" && data.message.trim()
          ? data.message
          : "Missing content generated successfully.";
      showSuccess(msg);
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to generate missing content.",
      );
    },
    onSettled: () => {
      useMainStore.getState().setIsGeneratingCampaignContent(false);
    },
  });
};

export const useFetchCampaignIndividualSequencingContent = ({
  campaignId,
  prospectId,
  enrollmentId,
  enabled = true,
  refetchInterval,
}) => {
  return useQuery({
    queryKey: [
      "campaign-individual-sequencing-content",
      campaignId,
      prospectId,
      enrollmentId,
    ],
    queryFn: () =>
      fetchCampaignIndividualSequencingContent({ campaignId, prospectId }),
    enabled: enabled && !!campaignId && !!prospectId,
    // refetchOnMount/refetchOnWindowFocus "always" defeated the cache the same
    // way the list queries used to (see LIST_STALE_TIME above): stepping back to
    // a prospect paid a full round trip even though the answer had not changed.
    // Content that is still being written keeps itself fresh through
    // refetchInterval, and the mutations invalidate this key on write.
    staleTime: SEQUENCE_CONTENT_STALE_TIME,
    retry: 1,
    refetchInterval,
  });
};

export const useRegenerateCampaignIndividualSequencingContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, prospectId }) =>
      fetchCampaignIndividualSequencingContent({
        campaignId,
        prospectId,
        forceRegenerate: true,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [
          "campaign-individual-sequencing-content",
          variables.campaignId,
          variables.prospectId,
          variables.enrollmentId,
        ],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [
          "campaign-individual-sequencing-content",
          variables.campaignId,
          variables.prospectId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "content-writer-status",
          variables.campaignId,
          variables.prospectId,
        ],
      });
      showSuccess("Content updated with your latest AI Trainer memory.");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to update content with the latest memory.",
      );
    },
  });
};

export const useContentWriterStatus = ({
  campaignId,
  prospectId,
  enrollmentId,
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["content-writer-status", campaignId, prospectId, enrollmentId],
    queryFn: () =>
      getContentWriterStatus({
        campaignId,
        contactId: prospectId,
      }),
    enabled: enabled && !!campaignId && !!prospectId,
    refetchOnMount: "always",
    refetchInterval: enabled ? 15_000 : false,
    staleTime: 0,
  });
};

// update campaign individual sequencing content hook
export const useUpdateCampaignIndividualSequencingContent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      prospectId,
      newsContent,
      selectedSignalTrigger,
      selectedSignalTheme,
    }) =>
      updateCampaignIndividualSequencingContent({
        campaignId,
        prospectId,
        newsContent,
        selectedSignalTrigger,
        selectedSignalTheme,
      }),
    onSuccess: (data, variables) => {
      showSuccess(
        "Campaign individual sequencing content updated successfully!",
      );
      queryClient.invalidateQueries({
        queryKey: [
          "campaign-individual-sequencing-content",
          variables.campaignId,
          variables.prospectId,
        ],
      });
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message ||
          "Failed to update campaign individual sequencing content",
      );
    },
  });
};

// add campaign asset hook
export const useAddCampaignAsset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetData, description, onUploadProgress }) =>
      addCampaignAsset({ assetData, description, onUploadProgress }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-assets"] });
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to add asset");
    },
  });
};

function normalizeDeleteAssetVariables(variables) {
  if (variables != null && typeof variables === "object" && "assetId" in variables) {
    return {
      assetId: variables.assetId,
      suppressSuccessToast: Boolean(variables.suppressSuccessToast),
    };
  }
  return {
    assetId: variables,
    suppressSuccessToast: false,
  };
}

// delete campaign asset hook
export const useDeleteCampaignAsset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      const { assetId } = normalizeDeleteAssetVariables(variables);
      return deleteCampaignAsset(assetId);
    },
    onSuccess: (_data, variables) => {
      const { suppressSuccessToast } = normalizeDeleteAssetVariables(variables);
      if (!suppressSuccessToast) {
        showSuccess("Asset deleted successfully!");
      }
      queryClient.invalidateQueries({ queryKey: ["campaign-assets"] });
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to delete asset");
    },
  });
};

// update campaign step content user changes hook
export const useUpdateCampaignStepContentUserChanges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, payload }) =>
      updateCampaignStepContentUserChanges({ stepId, payload }),
    onSuccess: (data, _variables) => {
      showSuccess("Campaign step content updated successfully!");
      queryClient.invalidateQueries({
        queryKey: [
          "campaign-individual-sequencing-content",
          data?.data?.campaign_id,
          data?.data?.contact_id,
        ],
      });
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update campaign step content");
    },
  });
};

// get analytics aggregated data for campaign dashboard hook
export const useGetAnalyticsAggregatedDataForCampaignDashboard = (
  campaignId,
  from,
  to,
  scope = "current",
) => {
  return useQuery({
    queryKey: ["analytics-aggregated-data", campaignId, from, to, scope],
    queryFn: () =>
      getAnalyticsAggregatedDataForCampaignDashboard(campaignId, from, to, scope),
    enabled: !!campaignId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000,
    retry: 1,
  });
};

// delete lead(s) hook - accepts single id or array of ids
export const useDeleteLead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => deleteLead(ids),
    onSuccess: (response, ids) => {
      const count = Array.isArray(ids) ? ids.length : 1;
      showSuccess(
        count > 1
          ? `${count} leads deleted successfully!`
          : "Lead deleted successfully!",
      );
      for (const item of response?.data || []) {
        const campaignId = item?.enrollment?.campaign_id;
        const contactId = item?.enrollment?.contact_id;
        const enrollmentId = item?.enrollment?.id;
        if (!campaignId || !contactId || !enrollmentId) continue;
        queryClient.removeQueries({
          queryKey: ["campaign-individual-sequencing-content", campaignId, contactId, enrollmentId],
          exact: true,
        });
        queryClient.removeQueries({
          queryKey: ["content-writer-status", campaignId, contactId, enrollmentId],
          exact: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["campaign-leads"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-leads-infinite"] });
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to delete lead. Please try again.");
    },
  });
};


/** Sent with mutate(vars) to force POST /campaign/:id/:action (avoids stale closure after setState). */
export const CAMPAIGN_HTTP_ACTION_KEY = "__httpAction";

// campaign status action hook
export const useCampaignStatusAction = ({ action, campaignId }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      let actionToUse = action;
      let body = payload;
      if (
        payload &&
        typeof payload === "object" &&
        payload[CAMPAIGN_HTTP_ACTION_KEY]
      ) {
        const { [CAMPAIGN_HTTP_ACTION_KEY]: explicit, ...rest } = payload;
        actionToUse = explicit;
        body = Object.keys(rest).length ? rest : undefined;
      }
      return campaignStatusAction({
        campaignId,
        action: actionToUse,
        payload: body,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaign-readiness", campaignId] });
      let label = action;
      if (
        variables &&
        typeof variables === "object" &&
        variables[CAMPAIGN_HTTP_ACTION_KEY]
      ) {
        label = variables[CAMPAIGN_HTTP_ACTION_KEY];
      }
      showSuccess(
        "Campaign status changed to " +
          (label?.charAt(0).toUpperCase() + label?.slice(1).toLowerCase()),
      );
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update campaign status");
    },
  });
};

// get analytics aggregated data for campaign dashboard by channel hook
export const useGetAnalyticsAggregatedDataForCampaignDashboardByChannel = (
  campaignId,
  action,
  channel,
  from,
  to,
  scope = "current",
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [
      "analytics-aggregated-data-by-channel",
      campaignId,
      action,
      channel,
      from,
      to,
      scope,
    ],
    queryFn: async () => {
      const contacts = await getAnalyticsAggregatedDataForCampaignDashboardByChannel(
        campaignId,
        action,
        channel,
        from,
        to,
        scope,
      );
      // Opening/refetching the drill-down must also refresh its summary card.
      await queryClient.invalidateQueries({
        queryKey: ["analytics-aggregated-data", campaignId, from, to, scope],
        exact: true,
      });
      console.info("[CampaignAnalytics][contacts]", { campaignId, action, channel, from, to, scope, count: contacts?.length ?? 0 });
      return contacts;
    },
    enabled: !!campaignId && !!action && !!channel,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000,
    retry: 1,
  });
};
